import React, { FC } from 'react';
import './coin-wallet.css';

export type CoinPileSize = 'sm' | 'md' | 'lg' | 'xl' | 'hero';

export interface CoinPileProps {
  /** Optional badge; abbreviated above 999 (1.2k). */
  amount?: number;
  size?: CoinPileSize;
  /** Slow glint + settle. Always off under prefers-reduced-motion. */
  animated?: boolean;
  className?: string;
  /** Accessible label. Decorative (aria-hidden) when omitted. */
  label?: string;
}

const SIZE_PX: Record<CoinPileSize, number> = {
  sm: 32,
  md: 48,
  lg: 64,
  xl: 80,
  hero: 112,
};

// Two short stacks of lying coins (bottom → top), positions in % of the box.
const DISCS: ReadonlyArray<{ left: number; bottom: number }> = [
  { left: 4, bottom: 14 },
  { left: 4, bottom: 24 },
  { left: 4, bottom: 34 },
  { left: 4, bottom: 44 },
  { left: 26, bottom: 6 },
  { left: 26, bottom: 16 },
];

export const formatPileAmount = (amount: number): string =>
  amount > 999
    ? `${(amount / 1000).toFixed(amount >= 10_000 ? 0 : 1)}k`
    : String(Math.max(0, Math.trunc(amount)));

/**
 * A small 3D gold coin pile drawn purely with CSS (no images, no deps):
 * stacked rimmed discs plus one standing coin with a ♠ mark.
 */
export const CoinPile: FC<CoinPileProps> = ({
  amount,
  size = 'lg',
  animated = false,
  className = '',
  label,
}) => {
  const px = SIZE_PX[size] ?? SIZE_PX.lg;
  return (
    <span
      className={`cw-pile ${animated ? 'cw-pile--animated' : ''} ${className}`.trim()}
      style={{ '--cw-pile-size': `${px}px` } as React.CSSProperties}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <span className="cw-pile__shadow" />
      {DISCS.map((d, i) => (
        <span
          key={i}
          className={`cw-pile__disc ${i === 3 ? 'cw-pile__disc--top' : ''}`}
          style={{ left: `${d.left}%`, bottom: `${d.bottom}%` }}
        />
      ))}
      <span className="cw-pile__face">
        <span className="cw-pile__pip">♠</span>
        <span className="cw-pile__glint" />
      </span>
      {amount !== undefined && (
        <span className="cw-pile__badge">{formatPileAmount(amount)}</span>
      )}
    </span>
  );
};
